import React, { memo, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import Heading from '@atlaskit/heading';
import CheckCircleIcon from '@atlaskit/icon/core/migration/success--check-circle';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import JiraProgressBar from '@atlassian/jira-common-components-progress-bar/src/main.tsx';
import ProgressElement from '@atlassian/jira-common-components-progress-bar/src/progress-element/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import { cardColor } from '../card/constants.tsx';
import {
	DONE_COLOR,
	EPIC_VERSION_PROGRESS_BAR_HEIGHT,
	EPIC_VERSION_PROGRESS_BAR_HOVER_PADDING,
	IN_PROGRESS_COLOR,
	PROGRESS_BAR_WRAPPER_TEST_ID,
	LOADING_COLOR,
	TODO_COLOR,
} from './constants.tsx';
import { messages } from './messages.tsx';
import type { ProgressBarProps } from './types.tsx';

const ProgressBar = ({
	hasMessage = false,
	progress,
	confirmMessage = '',
	isLoading,
}: ProgressBarProps) => {
	const { formatMessage } = useIntl();

	const { toDo, inProgress, done } = progress;
	const total = toDo.issueCount + inProgress.issueCount + done.issueCount;

	const toDoTooltipMessage = expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
		? messages.toDoToolTipsTextIssueTermRefresh
		: messages.toDoToolTipsText;
	const inProgressTooltipMessage = expVal(
		'issue-terminology-refresh-m2-replace',
		'isEnabled',
		false,
	)
		? messages.inProgressToolTipsTextIssueTermRefresh
		: messages.inProgressToolTipsText;
	const doneTooltipMessage = expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
		? messages.doneToolTipsTextIssueTermRefresh
		: messages.doneToolTipsText;

	return (
		<ProgressBarContainer>
			<Heading size="xxsmall" as="div">
				<MessageWrapper hasMessage={hasMessage}>
					<Box xcss={iconWrapperStyles}>
						<CheckCircleIcon
							color={token('color.icon.success')}
							LEGACY_size="small"
							testId="software-backlog-panel.common.ui.progress-bar.check-circle-icon"
							label=""
						/>
					</Box>
					<Message>{confirmMessage}</Message>
				</MessageWrapper>
			</Heading>
			<ProgressBarWrapper hasMessage={hasMessage} data-testid={PROGRESS_BAR_WRAPPER_TEST_ID}>
				<JiraProgressBar
					height={EPIC_VERSION_PROGRESS_BAR_HEIGHT}
					sum={Math.max(total, 1)}
					backgroundColor={isLoading ? LOADING_COLOR : TODO_COLOR}
					remainingTooltipContent={formatMessage(toDoTooltipMessage, {
						count: toDo.issueCount,
						total,
					})}
					aria-label={formatMessage(toDoTooltipMessage, {
						count: toDo.issueCount,
						total,
					})}
					tooltipHoverPadding={EPIC_VERSION_PROGRESS_BAR_HOVER_PADDING}
				>
					<ProgressElement
						value={done.issueCount}
						color={DONE_COLOR}
						tooltipContent={formatMessage(doneTooltipMessage, {
							count: done.issueCount,
							total,
						})}
						tooltipHoverPadding={EPIC_VERSION_PROGRESS_BAR_HOVER_PADDING}
					/>
					<ProgressElement
						value={inProgress.issueCount}
						color={IN_PROGRESS_COLOR}
						tooltipContent={formatMessage(inProgressTooltipMessage, {
							count: inProgress.issueCount,
							total,
						})}
						tooltipHoverPadding={EPIC_VERSION_PROGRESS_BAR_HOVER_PADDING}
					/>
				</JiraProgressBar>
			</ProgressBarWrapper>
		</ProgressBarContainer>
	);
};

export { ProgressBar };

export default memo<ProgressBarProps>(ProgressBar);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ProgressBarContainer = styled.div({
	position: 'relative',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MessageWrapper = styled.div<{
	hasMessage: boolean;
	children?: ReactNode;
}>({
	display: 'flex',
	alignItems: 'center',
	maxWidth: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	color: cardColor.default.textColor,
	padding: '0',
	marginTop: 0,
	marginRight: token('space.025'),
	marginBottom: 0,
	marginLeft: token('space.025'),
	// eslint-disable-next-line @typescript-eslint/no-explicit-any, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	opacity: (props: any) => (props.hasMessage ? '1' : '0'),
	position: 'absolute',
	transition: '0.5s',
	transitionProperty: 'opacity',
	pointerEvents: 'none', // this component is in front of progress bar so remove its mouse events
});

const iconWrapperStyles = xcss({
	paddingRight: 'space.050',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Message = styled.div({
	flex: '1 1 auto',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	overflow: 'hidden',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ProgressBarWrapper = styled.div<{
	hasMessage: boolean;
}>({
	marginTop: token('space.050'),
	marginRight: token('space.025'),
	marginBottom: token('space.050'),
	marginLeft: token('space.025'),
	// eslint-disable-next-line @typescript-eslint/no-explicit-any, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	opacity: (props: any) => (props.hasMessage ? '0' : '1'),
	transition: '0.5s',
	transitionProperty: 'opacity',
});
